/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { useContextProps } from 'react-aria-components/slots';
import { ClassificationBannerContext } from './context';
import styles from './styles.module.css';
import type { ClassificationBannerProps } from './types';

const fallbackContent = {
  missing: 'Missing',
  unclassified: 'Unclassified',
  cui: 'CUI',
  confidential: 'Confidential',
  secret: 'Secret',
  'top-secret': 'Top Secret',
  'ts-sci': 'Top Secret/SCI',
} as const;

/**
 * ClassificationBanner - A prominent banner for displaying security classification
 *
 * Provides a full-width banner component for displaying security classification
 * information at the top or bottom of pages. Essential for applications handling
 * classified or sensitive information requiring clear visual indicators.
 *
 * @param props - The classification banner props.
 * @param props.ref - Reference to the banner element.
 * @param props.children - Custom text content (defaults to variant label).
 * @param props.className - Additional CSS class names for styling.
 * @param props.variant - Classification level variant.
 * @returns The classification banner component.
 *
 * @example
 * ```tsx
 * // Top classification banner
 * <ClassificationBanner variant="confidential" />
 * ```
 */
export function ClassificationBanner({
  ref,
  ...props
}: ClassificationBannerProps) {
  [props, ref] = useContextProps(
    props,
    ref ?? null,
    ClassificationBannerContext,
  );

  const { children, className, variant = 'missing', ...rest } = props;

  return (
    <div
      {...rest}
      ref={ref}
      className={clsx(styles.banner, styles[variant], className)}
    >
      {children || fallbackContent[variant]}
    </div>
  );
}
